import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import Home from './pages/Home';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import { Suspense } from 'react';
import { supabase } from './lib/supabase';
import { useState, useEffect } from 'react';
import * as LazyComponents from './routes/lazy';

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
  </div>
);

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>;
}

// Admin Route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    async function checkAdminStatus() {
      if (!user) {
        setIsAdmin(false);
        setChecking(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          setIsAdmin(false);
          setChecking(false);
          return;
        }

        setIsAdmin(data?.role === 'admin');
        setChecking(false);
      } catch {
        setIsAdmin(false);
        setChecking(false);
      }
    }

    checkAdminStatus();
  }, [user]);

  if (isLoading || checking) {
    return <LoadingSpinner />;
  }

  if (!user || !isAdmin) {
    return <Navigate to="/dashboard" />;
  }

  return <Suspense fallback={<LoadingSpinner />}>{children}</Suspense>;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route 
        path="/terms" 
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <LazyComponents.TermsOfService />
          </Suspense>
        } 
      />
      <Route 
        path="/privacy" 
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <LazyComponents.PrivacyPolicy />
          </Suspense>
        } 
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <LazyComponents.Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <LazyComponents.Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/image"
        element={
          <ProtectedRoute>
            <LazyComponents.ImageGenerationTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/overlay"
        element={
          <ProtectedRoute>
            <LazyComponents.TextOverlayTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/speech"
        element={
          <ProtectedRoute>
            <LazyComponents.TestSpeechPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/music"
        element={
          <ProtectedRoute>
            <LazyComponents.MusicTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/video"
        element={
          <ProtectedRoute>
            <LazyComponents.VideoTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/minimaxi-video"
        element={
          <ProtectedRoute>
            <LazyComponents.MinimaxiVideoTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/photo-to-poem"
        element={
          <ProtectedRoute>
            <LazyComponents.PoemTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/video-overlay"
        element={
          <ProtectedRoute>
            <LazyComponents.VideoOverlayTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/test/stripe"
        element={
          <ProtectedRoute>
            <LazyComponents.StripeTest />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-result"
        element={
          <ProtectedRoute>
            <LazyComponents.PaymentResult />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <AdminRoute>
            <LazyComponents.AdminDashboard />
          </AdminRoute>
        }
      />
    </Routes>
  );
} 